/**
 * The rules behind the player's Auction Details page (#237, PL-019.2: Player/AuctionDetails.jsx)
 * and the bid area it hosts (the parts of #238, PL-019.3, needed to bid there), kept pure so they
 * can be tested on their own.
 *
 * The page reads one auction with every primary lot (Player/GetAuctionDetails,
 * usp_GetPlayerAuctionDetails) and merges the lot pushes into it (ReceiveBidStatus,
 * ReceiveCurrentBid). The server decides every status, price and winner; these functions only map
 * them to the screen. The lots arrive in run order, so a lot's place in the list is its team number
 * ("Team N of M").
 */

import { badgeFor, formatStart, pushedAuctionStatus } from './auctionList';
import { formatUsdWhole } from './money';
import { parseServerUtc } from './lotClock';

/**
 * The lot fields a push carries with the same meaning as the page's read, and so may replace. Never
 * startingBid: a push carries the top bid under that name, the read the configured starting bid.
 */
export const DETAIL_LOT_FIELDS = [
    'status',
    'topBidAmount',
    'minNextBid',
    'currentBidAmount',
    'totalPlacedBids',
    'bidPlacedBy',
    'bidPlacedByName',
    'endsAtUtc',
    'remainingBidTime',
];

/**
 * Reads Player/GetAuctionDetails' reply (the axios response, or what the wrapper returned).
 *
 * @param {*} res The reply.
 * @returns {{details: object}|{notFound: true}|null} null when there was no answer - no reply, a
 *   failure - so the page keeps what it shows.
 */
export const readDetailsReply = (res) => {
    const body = res?.data && typeof res.data === 'object' ? res.data : null;
    if (!body || res.status !== 200) return null;
    if (body.status === true) return { details: body };
    if (body.status === false) return { notFound: true };
    return null;
};

/**
 * The lots as the page keeps them: in the server's run order, each with its place in it.
 *
 * @param {Array} lots The read's lots.
 * @returns {Array}
 */
export const withPositions = (lots) =>
    (Array.isArray(lots) ? lots : []).map((lot, index) => ({ ...lot, position: index + 1 }));

/** The three groups of the team list, in their order. */
export const TEAM_STATES = { LIVE: 'live', UPCOMING: 'upcoming', SOLD: 'sold' };

/**
 * Which group a lot is in: Bidding now (Live, or Paused, which is still running), Up next (not
 * opened yet), or Sold (bidding has ended, with or without a winner).
 *
 * @param {object} lot A lot.
 * @returns {'live'|'upcoming'|'sold'}
 */
export const teamState = (lot) => {
    if (lot?.status === 'Live' || lot?.status === 'Paused') return TEAM_STATES.LIVE;
    if (lot?.status === 'Closed' || lot?.status === 'Archive') return TEAM_STATES.SOLD;
    return TEAM_STATES.UPCOMING;
};

/** Whether anyone has bid on the lot: topBidAmount is null until someone does. */
export const hasBids = (lot) => lot?.topBidAmount !== null && lot?.topBidAmount !== undefined;

/** Whether the player leads the lot (or, once it is over, won it). */
export const isLeader = (lot, userId) =>
    Number(lot?.bidPlacedBy) > 0 && Number(lot?.bidPlacedBy) === Number(userId);

const leaderName = (lot, userId, you) => (isLeader(lot, userId) ? you : lot?.bidPlacedByName || 'another player');

/** "Won by [First Last]", or "Won by you". */
export const wonByText = (lot, userId) => `Won by ${leaderName(lot, userId, 'you')}`;

/**
 * The lot being auctioned now: the one Live, else the one Paused; null between two lots and outside
 * the auction's run.
 *
 * @param {Array} lots The lots.
 * @returns {object|null}
 */
export const runningLotOf = (lots) =>
    (lots || []).find((lot) => lot?.status === 'Live') ?? (lots || []).find((lot) => lot?.status === 'Paused') ?? null;

/**
 * The team selected when the page opens (#237 AC6, AC9): the one being auctioned now; otherwise the
 * next one to open (Team 1 before the start); otherwise the first. Applied once - after that the
 * selection stays on its team as the team changes state (AC15).
 *
 * @param {Array} lots The lots.
 * @returns {number|null} The team id.
 */
export const defaultTeamId = (lots) => {
    const lot =
        runningLotOf(lots) ?? (lots || []).find((item) => teamState(item) === TEAM_STATES.UPCOMING) ?? (lots || [])[0];
    return lot ? lot.teamId : null;
};

/**
 * The auction's status from its lots, which the page keeps up to date from the pushes; between two
 * lots, when they cannot tell, the status the server read.
 *
 * @param {object} details The read.
 * @param {Array} lots The lots.
 * @returns {string} Upcoming, Live, Paused, Closed or Archive.
 */
export const auctionStatusOf = (details, lots) =>
    pushedAuctionStatus(lots || []) ?? details?.auctionStatus ?? 'Upcoming';

/**
 * What the header's status area shows: the LIVE label for a running (or paused) auction, the
 * "Starts in" countdown before the start, a Closed badge once every team is sold.
 *
 * @param {string} status auctionStatusOf's answer.
 * @returns {'live'|'upcoming'|'closed'}
 */
export const statusArea = (status) => badgeFor(status);

const GROUPS = [
    { key: TEAM_STATES.LIVE, label: 'Bidding now' },
    { key: TEAM_STATES.UPCOMING, label: 'Up next' },
    { key: TEAM_STATES.SOLD, label: 'Sold' },
];

/**
 * A group's text when it shows no team (#237): a search that matched none of its teams, an auction
 * that has not started yet (Bidding now), or nothing in it so far.
 *
 * @param {string} key The group.
 * @param {{searching: boolean, auctionStarted: boolean}} options
 * @returns {string}
 */
export const groupEmptyText = (key, { searching, auctionStarted }) => {
    if (searching) return 'No matching teams';
    if (key === TEAM_STATES.LIVE && !auctionStarted) return 'Bidding starts when the auction goes live';
    return 'None yet';
};

/**
 * The left panel's three groups, in lot order, each narrowed to the teams whose name contains the
 * search (AC4), with its empty text.
 *
 * @param {Array} lots The lots.
 * @param {{query?: string, auctionStarted: boolean}} options
 * @returns {Array<{key: string, label: string, items: Array, empty: string|null}>}
 */
export const teamGroups = (lots, { query = '', auctionStarted }) => {
    const search = String(query).trim().toLowerCase();
    return GROUPS.map((group) => {
        const items = (lots || []).filter(
            (lot) =>
                teamState(lot) === group.key &&
                (!search || String(lot?.teamName ?? '').toLowerCase().includes(search))
        );
        return {
            ...group,
            items,
            empty: items.length > 0 ? null : groupEmptyText(group.key, { searching: search !== '', auctionStarted }),
        };
    });
};

/**
 * A team's line in the left panel, but for the running team's clock, which the page draws itself:
 * the second line and the amount with its label (AC3).
 *
 * @param {object} lot The lot.
 * @param {{nextTeamId: number|null, userId: *}} options nextTeamId: the first team not opened yet.
 * @returns {{meta: string|null, amount: *, amountLabel: string}}
 */
export const teamListItem = (lot, { nextTeamId, userId }) => {
    switch (teamState(lot)) {
        case TEAM_STATES.LIVE:
            return hasBids(lot)
                ? { meta: null, amount: lot.topBidAmount, amountLabel: 'Highest' }
                : { meta: null, amount: lot.startingBid, amountLabel: 'Start' };
        case TEAM_STATES.UPCOMING:
            return {
                meta: `Team ${lot.position} • ${lot.teamId === nextTeamId ? 'Up next' : 'Queued'}`,
                amount: lot.startingBid,
                amountLabel: 'Start',
            };
        default:
            return hasBids(lot)
                ? { meta: wonByText(lot, userId), amount: lot.topBidAmount, amountLabel: 'Sold' }
                : { meta: 'No bids', amount: null, amountLabel: 'Unsold' };
    }
};

/**
 * The selected team section (#237 AC10-AC12): its heading, what stands right of it (the running
 * team's timer is drawn by the page), the bid shown with its label and the line under it, and the
 * Next Minimum Bid field ("—" once bidding has ended).
 *
 * A team closed with no bids is not covered by the ticket: it reads Closed, Unsold, with no value.
 *
 * @param {object} lot The selected lot.
 * @param {{userId: *}} options
 * @returns {{heading: string, badge: string|null, valueLabel: string, value: *, neutral: boolean,
 *   line: string, nextMinimumBid: *}}
 */
export const selectedTeamView = (lot, { userId }) => {
    switch (teamState(lot)) {
        case TEAM_STATES.LIVE:
            return {
                heading: 'Currently Bidding',
                badge: null,
                valueLabel: hasBids(lot) ? 'Highest Bid' : 'Starting Bid',
                value: hasBids(lot) ? lot.topBidAmount : lot.startingBid,
                neutral: false,
                line: hasBids(lot) ? `Highest bidder: ${leaderName(lot, userId, 'You')}` : 'No bids yet',
                nextMinimumBid: lot.minNextBid,
            };
        case TEAM_STATES.UPCOMING:
            return {
                heading: 'Up Next',
                badge: 'upcoming',
                valueLabel: 'Starting Bid',
                value: lot.startingBid,
                neutral: true,
                line: 'No bids yet',
                nextMinimumBid: lot.minNextBid,
            };
        default:
            return hasBids(lot)
                ? {
                    heading: 'Sold',
                    badge: 'sold',
                    valueLabel: 'Winning Bid',
                    value: lot.topBidAmount,
                    neutral: false,
                    line: wonByText(lot, userId),
                    nextMinimumBid: null,
                }
                : {
                    heading: 'Closed',
                    badge: 'unsold',
                    valueLabel: 'Winning Bid',
                    value: null,
                    neutral: true,
                    line: 'No bids',
                    nextMinimumBid: null,
                };
    }
};

const toCents = (value) => Math.round(Number(value) * 100);

/** The auction's pool: the sum of every lot's top bid, as the server adds it up (#236). */
export const poolOf = (lots) =>
    (lots || []).reduce((sum, lot) => sum + (hasBids(lot) ? toCents(lot.topBidAmount) : 0), 0) / 100;

/**
 * The per-team time limit (BidTime, minutes) as the stats row shows it: "1 min", "30 sec",
 * "1 min 30 sec".
 *
 * @param {*} minutes The auction's BidTime.
 * @returns {string} "-" when there is none.
 */
export const bidTimeLimitText = (minutes) => {
    const seconds = Math.round(Number(minutes) * 60);
    if (!Number.isFinite(seconds) || seconds <= 0) return '-';
    const parts = [];
    if (seconds >= 60) parts.push(`${Math.floor(seconds / 60)} min`);
    if (seconds % 60) parts.push(`${seconds % 60} sec`);
    return parts.join(' ');
};

/**
 * The stats row (#237 AC8): the teams, the pool, every bid on every team, the time limit.
 *
 * @param {object} details The read.
 * @param {Array} lots The lots.
 * @returns {{teams: number, pool: number, totalBids: number, bidTimeLimit: string}}
 */
export const statsOf = (details, lots) => ({
    teams: (lots || []).length,
    pool: poolOf(lots),
    totalBids: (lots || []).reduce((sum, lot) => sum + (Number(lot?.totalPlacedBids) || 0), 0),
    bidTimeLimit: bidTimeLimitText(details?.bidTime),
});

/**
 * The Auction Winnings rows (#237 AC14): "Place N – P%" and the server's amount. The places are
 * ranked as the server pays them (fn_AuctionPrizes: the highest percentage first); the server lists
 * them by title, so ties keep that order.
 *
 * @param {Array|undefined} winnings Auction/WinnerPercentage's rows.
 * @returns {Array<{place: number, percentage: number, amount: *}>}
 */
export const payoutRows = (winnings) =>
    (Array.isArray(winnings) ? winnings : [])
        .map((row, index) => ({ row, index }))
        .sort((a, b) => Number(b.row?.winnerPercentage) - Number(a.row?.winnerPercentage) || a.index - b.index)
        .map(({ row }, rank) => ({
            place: rank + 1,
            percentage: Number(row?.winnerPercentage),
            amount: row?.winningAmount,
        }));

const pad = (value) => String(value).padStart(2, '0');

/**
 * The "Starts in" countdown (#237 AC6): "2d 4h" from 24 hours, hh:mm:ss under.
 *
 * @param {number} seconds Seconds left.
 * @returns {string}
 */
export const formatStartsIn = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    if (total >= 86400) return `${Math.floor(total / 86400)}d ${Math.floor((total % 86400) / 3600)}h`;
    return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
};

/**
 * The deadline of the "Starts in" countdown on the device clock: the moment the read arrived plus
 * the time the server said was left, so the device clock's error cancels out (#191).
 *
 * @param {object} details The read: startDate (UTC, no zone) and serverNowUtc.
 * @param {number} receivedAt Date.now() when the read arrived.
 * @returns {number|null} Client epoch ms.
 */
export const startsInDeadline = (details, receivedAt) => {
    const start = parseServerUtc(details?.startDate);
    const serverNow = parseServerUtc(details?.serverNowUtc);
    if (!Number.isFinite(start)) return null;
    return Number.isFinite(serverNow) ? receivedAt + (start - serverNow) : start;
};

// --- The bid area (#238, the parts needed to bid on this page) ---------------------------------

/** What the bid area shows, one of these, checked in this order (#238). */
export const BID_AREA = {
    UNLOCK: 'unlock',
    CONTROLS: 'controls',
    PAUSED: 'paused',
    UNLOCK_BEFORE_START: 'unlock-before-start',
    NOT_STARTED: 'not-started',
    NOT_OPEN: 'not-open',
    SOLD: 'sold',
};

/**
 * The bid area's content for the selected team: the Unlock prompt or the bidding controls on the
 * team being auctioned (an info line instead while the auction is paused, which the ticket does not
 * cover); before the start, the auction's info; then the team's.
 *
 * #238 AC8: before the start, a player who has not unlocked the auction also gets the Unlock prompt,
 * above the start line (UNLOCK_BEFORE_START). A pass is sold only then (the rule that makes
 * cancellation credits correct), while the ticket's prompt shows only on a live team, where the
 * server refuses the sale.
 *
 * @param {object} lot The selected lot.
 * @param {{canBid: boolean, auctionStarted: boolean}} options canBid: the auction is unlocked.
 * @returns {string} One of BID_AREA.
 */
export const bidAreaContent = (lot, { canBid, auctionStarted }) => {
    const state = teamState(lot);
    if (state === TEAM_STATES.LIVE) {
        if (!canBid) return BID_AREA.UNLOCK;
        return lot.status === 'Paused' ? BID_AREA.PAUSED : BID_AREA.CONTROLS;
    }
    if (!auctionStarted) return canBid ? BID_AREA.NOT_STARTED : BID_AREA.UNLOCK_BEFORE_START;
    return state === TEAM_STATES.UPCOMING ? BID_AREA.NOT_OPEN : BID_AREA.SOLD;
};

/**
 * The bid area's info line for the contents that have no controls.
 *
 * @param {string} content bidAreaContent's answer.
 * @param {{lot: object, startDate: string, userId: *}} options
 * @returns {string}
 */
export const bidAreaInfo = (content, { lot, startDate, userId }) => {
    switch (content) {
        case BID_AREA.NOT_STARTED:
        case BID_AREA.UNLOCK_BEFORE_START:
            return `Bidding opens when the auction starts on ${formatStart(startDate)}. Teams are auctioned one at a time, starting with Team 1.`;
        case BID_AREA.NOT_OPEN:
            return 'Bidding on this team opens after the current team closes. Teams are auctioned one at a time.';
        case BID_AREA.PAUSED:
            return 'Bidding is paused for this auction.';
        default:
            return hasBids(lot)
                ? `Bidding for this team is closed. ${wonByText(lot, userId)} for ${formatUsdWhole(lot.topBidAmount)}.`
                : 'Bidding for this team is closed. Nobody bid on it.';
    }
};

/**
 * The three amount buttons (#238): the Next Minimum Bid, one increment above it and three.
 *
 * @param {*} minNextBid The lot's minimum next bid.
 * @param {*} increment The lot's bid increment.
 * @returns {Array<{amount: number, caption: string}>}
 */
export const amountOptions = (minNextBid, increment) => {
    const min = toCents(minNextBid);
    const step = toCents(increment) || 0;
    return [
        { amount: min / 100, caption: 'Minimum' },
        { amount: (min + step) / 100, caption: `+${formatUsdWhole(step / 100)}` },
        { amount: (min + 3 * step) / 100, caption: `+${formatUsdWhole((3 * step) / 100)}` },
    ];
};

/**
 * What a player may type in Custom Bid Amount: digits and at most two decimals, with any "$",
 * comma or space they paste dropped.
 *
 * @param {string} text The input's value.
 * @returns {string}
 */
export const cleanAmountInput = (text) => {
    const digits = String(text ?? '').replace(/[^0-9.]/g, '');
    const [whole, ...rest] = digits.split('.');
    return rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
};

export const BID_MESSAGES = {
    EMPTY: 'Enter a bid amount or pick one of the suggested amounts.',
    // The server's own refusal (usp_InsertBidModel, ALREADY_HIGHEST_BIDDER).
    LEADER: 'You are already the highest bidder on this team.',
    // The server's own refusal (usp_InsertBidModel, BID_NOT_POSITIVE), for a lot whose minimum is 0.
    NOT_POSITIVE: 'Your bid must be more than $0.',
};

/**
 * Checks a bid before it is confirmed and sent (#238): an amount, at least the Next Minimum Bid, on
 * an increment step above it ($170, $180, $190 for a $10 increment). The step is checked here
 * only; the server takes any amount from the minimum. A player who leads is told so, as the server
 * would. 0 is an amount below the minimum like any other, not an empty field (#301).
 *
 * @param {string} text Custom Bid Amount.
 * @param {{minNextBid: *, increment: *, leads: boolean}} rules
 * @returns {{amount: number}|{error: string}}
 */
export const validateBid = (text, { minNextBid, increment, leads }) => {
    const trimmed = String(text ?? '').trim();
    const amount = Number(trimmed);
    if (!trimmed || !Number.isFinite(amount)) return { error: BID_MESSAGES.EMPTY };
    if (leads) return { error: BID_MESSAGES.LEADER };
    const min = toCents(minNextBid);
    if (toCents(amount) < min) return { error: `Your bid must be at least ${formatUsdWhole(min / 100)}.` };
    if (amount <= 0) return { error: BID_MESSAGES.NOT_POSITIVE };
    const step = toCents(increment);
    if (step > 0 && (toCents(amount) - min) % step !== 0) {
        return { error: `Your bid must be in increments of ${formatUsdWhole(step / 100)}.` };
    }
    return { amount };
};

/** The line under the bidding controls. */
export const bidHint = (minNextBid, increment) =>
    `Minimum next bid ${formatUsdWhole(minNextBid)} • Increments of ${formatUsdWhole(increment)} • All bidding amounts are in USD`;

/** The confirmation dialog's question. */
export const confirmBidText = (amount, teamName) => `Place a bid of ${formatUsdWhole(amount)} on ${teamName}?`;

/** The message after an accepted bid. */
export const bidPlacedText = (amount) => `Your bid of ${formatUsdWhole(amount)} was placed. You are the highest bidder.`;
